import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery } from "@mui/material";
import Archive from "@mui/icons-material/Archive";
import CallSplit from "@mui/icons-material/CallSplit";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import Today from "@mui/icons-material/Today";
import Undo from "@mui/icons-material/Undo";
import moment from "moment";
import { MouseEvent, useState } from "react";
import { dateFormat } from "../../misc/dates";
import { useSnackbar } from "../../misc/Snackbar";
import { taskPermalink } from "./TaskPicker";

type Target<T> = {
    task: T;
    top: number;
    left: number;
};

type MenuTask = {
    id: number;
    team?: number;
    dueDate?: string | null;
    completedAt?: string | null;
};

// useTaskContextMenu gives a view one right-click menu that every row or item in
// it can open. Render `element` once and call `open` from onContextMenu, or `openAt`
// from a touch and hold. T is whatever the view holds for a task, so a view listing
// summaries gets its summary back rather than a Task it never loaded. An action is
// offered only when the view passes its handler.
export function useTaskContextMenu<T extends MenuTask>(props: {
    onArchive: (task: T) => void;
    onToggle?: (task: T, completed: boolean) => void;
    onToday?: (task: T) => void;
    onPromote?: (task: T) => void;

    // onRemove is offered on a touch screen only, where the row hides its own remove
    // button (see PanelRow). removeLabel names it.
    onRemove?: (task: T) => void;
    removeLabel?: string;
}) {
    const [target, setTarget] = useState<Target<T> | null>(null);
    const snack = useSnackbar();
    const touch = useMediaQuery("(hover: none)");

    const open = (task: T, e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setTarget({ task: task, top: e.clientY, left: e.clientX });
    };

    const openAt = (task: T, point: { x: number; y: number }) => {
        setTarget({ task: task, top: point.y, left: point.x });
    };

    // run closes the menu first, so the action's own feedback isn't hidden behind it
    const run = (action: (task: T) => void) => () => {
        const task = target?.task;
        setTarget(null);
        if (task) action(task);
    };

    const copyLink = async (task: T) => {
        try {
            await navigator.clipboard.writeText(taskPermalink(task));
            snack.success("Link copied");
        } catch (err: any) {
            snack.error(err.message);
        }
    };

    const task = target?.task;
    const done = !!task?.completedAt;
    const onToday = !!task && task.dueDate === moment().format(dateFormat);

    const element = (
        <Menu
            open={!!target}
            anchorReference="anchorPosition"
            anchorPosition={target ? { top: target.top, left: target.left } : undefined}
            onClose={() => setTarget(null)}
        >
            {props.onToggle && (
                <MenuItem onClick={run(task => props.onToggle?.(task, !done))}>
                    <ListItemIcon>{done ? <Undo fontSize="small" /> : <Check fontSize="small" />}</ListItemIcon>
                    <ListItemText>{done ? "Reopen" : "Complete"}</ListItemText>
                </MenuItem>
            )}
            {props.onToday && !done && !onToday && (
                <MenuItem onClick={run(task => props.onToday?.(task))}>
                    <ListItemIcon><Today fontSize="small" /></ListItemIcon>
                    <ListItemText>Move to today</ListItemText>
                </MenuItem>
            )}
            {props.onPromote && (
                <MenuItem onClick={run(task => props.onPromote?.(task))}>
                    <ListItemIcon><CallSplit fontSize="small" /></ListItemIcon>
                    <ListItemText>Make standalone task</ListItemText>
                </MenuItem>
            )}
            <MenuItem onClick={run(copyLink)}>
                <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Copy link</ListItemText>
            </MenuItem>
            <Divider />
            {props.onRemove && touch && (
                <MenuItem onClick={run(task => props.onRemove?.(task))}>
                    <ListItemIcon><Close fontSize="small" /></ListItemIcon>
                    <ListItemText>{props.removeLabel || "Remove"}</ListItemText>
                </MenuItem>
            )}
            <MenuItem onClick={run(props.onArchive)}>
                <ListItemIcon><Archive fontSize="small" /></ListItemIcon>
                <ListItemText>Archive</ListItemText>
            </MenuItem>
        </Menu>
    );

    return { open: open, openAt: openAt, element: element };
}
